import React, { useEffect, useState, useCallback } from "react";
import {
  Users, Search, Loader2, Phone, Mail, Building2, TrendingUp, UserPlus, Trophy,
  ClipboardList, ArrowRight, X, CheckCircle2, Clock, DollarSign, CalendarClock, CalendarCheck, BarChart3,
  Send, Award, Medal, Plus, Trash2, Save,
} from "lucide-react";
import { toast } from "sonner";
import { Head } from "./screens/shared";
import { sr } from "../../lib/sr";
import { auth } from "../../lib/api";

const _derivedRole = () => {
  const r = auth.getUser()?.role;
  if (["admin", "super_admin"].includes(r)) return "admin";
  if (["manager", "campaign_manager"].includes(r)) return "manager";
  return "rep";
};

const STAGES = ["new", "working", "qualified", "proposal", "won", "lost"];
const STAGE_LABEL = { new: "New", working: "Working", qualified: "Qualified", proposal: "Proposal", won: "Won", lost: "Lost" };
const STAGE_COLOR = { new: "#2563EB", working: "#7C3AED", qualified: "#0891B2", proposal: "#B8791B", won: "#067647", lost: "#9CA3AF" };
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;
const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

function StageBadge({ stage }) {
  return <span className="sr-badge" style={{ background: `${STAGE_COLOR[stage]}18`, color: STAGE_COLOR[stage] }}>{STAGE_LABEL[stage] || stage}</span>;
}

function LeadRow({ lead, onOpen }) {
  const c = lead.contact || {}; const b = lead.business || {};
  return (
    <button onClick={() => onOpen(lead)} data-testid={`lead-row-${lead.id}`}
      style={{ display: "grid", gridTemplateColumns: "1.5fr 1.3fr 0.8fr 1fr 0.9fr", gap: 8, padding: "12px 14px", alignItems: "center", width: "100%", textAlign: "left", cursor: "pointer", background: "#fff", border: "none", borderTop: "1px solid var(--border)" }}>
      <span><strong style={{ color: "var(--navy)" }}>{c.name || b.name || c.email || c.phone || "New lead"}</strong>
        <span className="sr-muted" style={{ display: "block", fontSize: 12 }}>{b.name || c.email || c.phone}</span></span>
      <span className="sr-muted" style={{ fontSize: 13 }}>{(lead.source || {}).form || "—"}{b.state ? ` · ${b.state}` : ""}</span>
      <StageBadge stage={lead.stage} />
      <span className="sr-muted" style={{ fontSize: 13 }}>{lead.owner_name || <em style={{ color: "var(--blue)" }}>Unassigned</em>}</span>
      <span style={{ fontSize: 13, color: "var(--navy)" }}>{lead.expected_value ? money(lead.expected_value) : "—"}</span>
    </button>
  );
}

function LeadDetail({ leadId, role, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [noteKind, setNoteKind] = useState("note");
  const [apptWhen, setApptWhen] = useState("");
  const [apptChannel, setApptChannel] = useState("call");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => sr.salesLead(leadId).then(setData).catch(() => toast.error("Couldn't load lead")), [leadId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const l = data.lead; const c = l.contact || {}; const b = l.business || {};
  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); await load(); onChanged && onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); } };
  const addNote = () => { if (!note.trim()) return; act(() => sr.salesActivity(leadId, { kind: noteKind, text: note }), "Logged").then(() => setNote("")); };
  const schedule = () => { if (!apptWhen) { toast.error("Pick a date & time"); return; } act(() => sr.salesSchedule(leadId, { when: new Date(apptWhen).toISOString(), channel: apptChannel }), "Follow-up scheduled").then(() => setApptWhen("")); };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(11,36,71,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }} data-testid="lead-detail-modal">
      <div className="sr-card" style={{ maxWidth: 780, width: "100%", maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 20 }}>{c.name || b.name || "Lead"}</h3>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 4 }} className="sr-muted">
              {c.email && <span style={{ fontSize: 13 }}><Mail size={12} style={{ verticalAlign: "-1px" }} /> {c.email}</span>}
              {c.phone && <span style={{ fontSize: 13 }}><Phone size={12} style={{ verticalAlign: "-1px" }} /> {c.phone}</span>}
              {b.name && <span style={{ fontSize: 13 }}><Building2 size={12} style={{ verticalAlign: "-1px" }} /> {b.name}{b.industry ? ` · ${b.industry}` : ""}{b.state ? ` · ${b.state}` : ""}</span>}
            </div>
          </div>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onClose} data-testid="lead-close"><X size={14} /></button>
        </div>

        {l.customer_id && <div className="sr-badge" style={{ background: "#DCFCE7", color: "#067647", marginBottom: 10 }} data-testid="lead-converted-badge"><CheckCircle2 size={12} /> Converted to customer</div>}
        {l.message && <p style={{ fontSize: 14, background: "var(--blue-light)", padding: 10, borderRadius: 8 }}>“{l.message}”</p>}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "12px 0" }}>
          <StageBadge stage={l.stage} />
          <select className="sr-select" value={l.stage} disabled={busy} data-testid="lead-stage-select"
            onChange={(e) => act(() => sr.salesPatch(leadId, { stage: e.target.value }), "Stage updated")} style={{ maxWidth: 160 }}>
            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
          </select>
          {!l.owner_id && <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} data-testid="lead-take"
            onClick={() => act(() => sr.salesTake(leadId), "You own this lead")}><UserPlus size={13} /> Take ownership</button>}
          <span className="sr-muted" style={{ fontSize: 13, marginLeft: "auto" }}>{l.owner_name ? `Owner: ${l.owner_name}` : "Unassigned"}</span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <div className="sr-field"><label className="sr-label">Expected value ($)</label>
            <input className="sr-input" type="number" defaultValue={l.expected_value || ""} data-testid="lead-value"
              onBlur={(e) => e.target.value && act(() => sr.salesPatch(leadId, { expected_value: parseFloat(e.target.value) }), "Saved")} /></div>
          <div className="sr-field"><label className="sr-label">Next action</label>
            <div className="sr-muted" style={{ fontSize: 13, paddingTop: 8 }}><Clock size={12} style={{ verticalAlign: "-1px" }} /> {l.next_action || "—"} · due {l.next_due ? fmt(l.next_due) : "—"}</div></div>
        </div>

        {(role === "admin" || role === "manager") && !l.customer_id && (
          <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginBottom: 14 }} disabled={busy} data-testid="lead-convert"
            onClick={() => act(() => sr.salesConvert(leadId, { mode: "create", service_mode: "MANAGED_SERVICE" }), "Converted to customer")}>
            <Trophy size={13} /> Convert to customer
          </button>
        )}

        <div className="sr-card" style={{ background: "#F8FAFC" }}>
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Log activity</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select className="sr-select" value={noteKind} onChange={(e) => setNoteKind(e.target.value)} data-testid="activity-kind" style={{ maxWidth: 130 }}>
              {["note", "call", "email", "sms", "meeting", "proposal"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input className="sr-input" placeholder="What happened?" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="activity-text" style={{ flex: 1, minWidth: 180 }} />
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={addNote} disabled={busy || !note.trim()} data-testid="activity-save">Log</button>
          </div>
        </div>

        <div className="sr-card" style={{ background: "#F8FAFC", marginTop: 12 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}><CalendarClock size={13} style={{ verticalAlign: "-2px" }} /> Schedule follow-up</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input className="sr-input" type="datetime-local" value={apptWhen} onChange={(e) => setApptWhen(e.target.value)} data-testid="appt-when" style={{ maxWidth: 220 }} />
            <select className="sr-select" value={apptChannel} onChange={(e) => setApptChannel(e.target.value)} data-testid="appt-channel" style={{ maxWidth: 130 }}>
              {["call", "meeting", "video"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <button className="sr-btn sr-btn-sm" onClick={schedule} disabled={busy || !apptWhen} data-testid="appt-save"><CalendarCheck size={13} /> Schedule</button>
          </div>
        </div>

        <div className="sr-card" style={{ background: "#F8FAFC", marginTop: 12 }} data-testid="lead-nurture-card">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}><Send size={13} style={{ verticalAlign: "-2px" }} /> Nurture automation</div>
          {(() => {
            const nz = l.nurture;
            if (nz && nz.status === "active") {
              return (
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="sr-badge" style={{ background: "#EEF4FF", color: "#0B4DA2", border: "1px solid #C9DDFB" }} data-testid="nurture-active">
                    Sequence {nz.sent}/{nz.total}{nz.next_step_at ? ` · next ${fmt(nz.next_step_at)}` : ""}
                  </span>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" disabled={busy} data-testid="nurture-stop"
                    onClick={() => act(() => sr.salesNurtureStop(leadId), "Nurture stopped")}>Stop sequence</button>
                  <span className="sr-muted" style={{ fontSize: 12 }}>Auto-stops when the lead replies, books, or converts.</span>
                </div>
              );
            }
            if (nz && (nz.status === "completed" || nz.status === "stopped")) {
              const label = nz.status === "completed" ? "Sequence complete" : `Sequence stopped${nz.stop_reason ? ` (${nz.stop_reason})` : ""}`;
              return (
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <span className="sr-muted" style={{ fontSize: 13 }} data-testid="nurture-done">{label} · {nz.sent}/{nz.total} sent</span>
                  {!l.customer_id && l.stage !== "won" && l.stage !== "lost" && (
                    <button className="sr-btn sr-btn-sm" disabled={busy} data-testid="nurture-restart"
                      onClick={() => act(() => sr.salesNurture(leadId), "Nurture restarted")}>Restart</button>
                  )}
                </div>
              );
            }
            if (l.customer_id || l.stage === "won" || l.stage === "lost") {
              return <p className="sr-muted" style={{ fontSize: 13, margin: 0 }}>This lead is closed — nurture doesn't apply.</p>;
            }
            return (
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} data-testid="nurture-start"
                  onClick={() => act(() => sr.salesNurture(leadId), "Nurture started")}><Send size={13} /> Start nurture sequence</button>
                <span className="sr-muted" style={{ fontSize: 12 }}>Sends a 3-touch email follow-up in business hours; stops the moment they reply or book.</span>
              </div>
            );
          })()}
        </div>

        <div style={{ marginTop: 14 }}>
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Timeline</div>
          {(data.activities || []).length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No activity yet.</p> :
            data.activities.map((a) => (
              <div key={a.id} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: "1px solid var(--border)" }} data-testid={`activity-${a.id}`}>
                <span className="sr-badge sr-badge-gray" style={{ height: "fit-content" }}>{a.kind}</span>
                <span style={{ flex: 1, fontSize: 13, color: "var(--navy)" }}>{a.text}</span>
                <span className="sr-muted" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmt(a.at)}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Reports() {
  const [r, setR] = useState(null);
  useEffect(() => { sr.salesReports().then(setR).catch(() => setR(null)); }, []);
  if (!r) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const stat = (label, value, icon) => (
    <div className="sr-card" style={{ flex: "1 1 150px" }}><div className="sr-muted" style={{ fontSize: 12 }}>{icon} {label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: "var(--navy)" }}>{value}</div></div>
  );
  return (
    <div data-testid="sales-reports">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        {stat("Total leads", r.total_leads, <ClipboardList size={12} />)}
        {stat("Converted", r.converted, <Trophy size={12} />)}
        {stat("Lead → customer", `${r.lead_to_customer_pct}%`, <TrendingUp size={12} />)}
        {stat("Pipeline value", money(r.estimated_pipeline_value), <DollarSign size={12} />)}
        {stat("Unassigned", r.unassigned, <Users size={12} />)}
        {stat("Overdue follow-ups", r.overdue_followups, <Clock size={12} />)}
        {stat("Avg first response", r.avg_first_response_hours != null ? `${r.avg_first_response_hours}h` : "—", <Clock size={12} />)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 10 }}>By stage</div>
          {STAGES.map((s) => <div key={s} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}><StageBadge stage={s} /><strong>{r.by_stage[s] || 0}</strong></div>)}</div>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 10 }}>Top salespeople</div>
          {(r.by_owner || []).length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No owned leads yet.</p> :
            r.by_owner.map((o) => <div key={o.owner_id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 14 }}>
              <span style={{ color: "var(--navy)" }}>{o.owner_name || o.owner_id.slice(0, 6)}</span>
              <span className="sr-muted">{o.won} won · {o.leads} leads · {money(o.pipeline_value)}</span></div>)}</div>
      </div>
      <div className="sr-card" style={{ marginTop: 14 }}><div className="sr-eyebrow" style={{ marginBottom: 10 }}>Source mix</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{Object.entries(r.source_mix || {}).map(([k, v]) => <span key={k} className="sr-badge sr-badge-gray">{k}: {v}</span>)}</div></div>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { sr.salesDashboard().then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const maxT = Math.max(1, ...d.trend.map((t) => Math.max(t.new_leads, t.conversions)));
  const stat = (label, value, icon, color) => (
    <div className="sr-card" style={{ flex: "1 1 150px" }}><div className="sr-muted" style={{ fontSize: 12 }}>{icon} {label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || "var(--navy)" }}>{value}</div></div>
  );
  return (
    <div data-testid="sales-dashboard">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        {stat("Verified customers", d.converted_customers, <CheckCircle2 size={12} />, "#067647")}
        {stat("Deals won", d.won_count, <Trophy size={12} />)}
        {stat("Won value", money(d.won_value), <DollarSign size={12} />, "#067647")}
        {stat("Open leads", d.open_leads, <ClipboardList size={12} />)}
        {stat("Replied < 24h", d.sla_within_24h_pct != null ? `${d.sla_within_24h_pct}%` : "—", <Clock size={12} />)}
        {stat("Follow-ups due", d.appointments_upcoming, <CalendarClock size={12} />)}
        {stat("Overdue", d.appointments_overdue, <Clock size={12} />, d.appointments_overdue ? "#B91C1C" : undefined)}
      </div>
      <div className="sr-card">
        <div className="sr-eyebrow" style={{ marginBottom: 12 }}><BarChart3 size={13} style={{ verticalAlign: "-2px" }} /> New leads vs conversions (6 months)</div>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", height: 160 }}>
          {d.trend.map((t) => (
            <div key={t.month} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ display: "flex", gap: 4, alignItems: "flex-end", justifyContent: "center", height: 120 }}>
                <div title={`${t.new_leads} new`} style={{ width: 14, height: `${t.new_leads / maxT * 100}%`, background: "var(--blue)", borderRadius: 4, minHeight: 2 }} />
                <div title={`${t.conversions} won`} style={{ width: 14, height: `${t.conversions / maxT * 100}%`, background: "#067647", borderRadius: 4, minHeight: 2 }} />
              </div>
              <div className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>{t.month.slice(5)}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 12 }} className="sr-muted">
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "var(--blue)", borderRadius: 2 }} /> New leads</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#067647", borderRadius: 2 }} /> Conversions</span>
        </div>
      </div>
    </div>
  );
}

function FollowUps({ onOpen }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => sr.salesAppointments().then(setD).catch(() => setD({ upcoming: [], overdue: [] })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const close = async (id, status) => { try { await sr.salesAppointmentStatus(id, status); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const Row = ({ a, overdue }) => (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 14px", borderTop: "1px solid var(--border)" }} data-testid={`appt-${a.id}`}>
      <CalendarClock size={16} color={overdue ? "#B91C1C" : "var(--blue)"} />
      <div style={{ flex: 1 }}>
        <button onClick={() => onOpen(a.lead_id)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}>
          <strong style={{ color: "var(--navy)" }}>{a.lead_name}</strong>
          <span className="sr-muted" style={{ display: "block", fontSize: 12 }}>{a.channel} · {a.owner_name || "Unassigned"}{a.notes ? ` · ${a.notes}` : ""}</span>
        </button>
      </div>
      <span style={{ fontSize: 13, color: overdue ? "#B91C1C" : "var(--navy)", whiteSpace: "nowrap" }}>{fmt(a.when)}</span>
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => close(a.id, "completed")} data-testid={`appt-done-${a.id}`}><CheckCircle2 size={13} /> Done</button>
    </div>
  );
  return (
    <div data-testid="sales-followups">
      {d.overdue.length > 0 && (
        <div className="sr-card" style={{ padding: 0, overflow: "hidden", marginBottom: 14, border: "1px solid #FCA5A5" }}>
          <div style={{ padding: "10px 14px", background: "#FEF2F2", color: "#B91C1C", fontWeight: 700, fontSize: 13 }}>Overdue ({d.overdue.length})</div>
          {d.overdue.map((a) => <Row key={a.id} a={a} overdue />)}
        </div>
      )}
      <div className="sr-card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px", background: "var(--blue-light)", fontWeight: 700, fontSize: 13, color: "var(--navy)" }}>Upcoming ({d.upcoming.length})</div>
        {d.upcoming.length === 0 ? <div className="sr-empty" style={{ padding: 24 }}>No follow-ups scheduled. Open a lead to schedule one.</div> :
          d.upcoming.map((a) => <Row key={a.id} a={a} />)}
      </div>
    </div>
  );
}

function Leaderboard() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); sr.salesLeaderboard(period).then(setD).catch(() => setD({ leaderboard: [] })); }, [period]);
  const medal = (rank) => rank === 1 ? "#D4AF37" : rank === 2 ? "#9CA3AF" : rank === 3 ? "#B87333" : null;
  return (
    <div data-testid="sales-leaderboard">
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[["month", "This month"], ["quarter", "Last 90 days"], ["all", "All time"]].map(([k, label]) => (
          <button key={k} onClick={() => setPeriod(k)} data-testid={`lb-period-${k}`}
            className={`sr-btn sr-btn-sm ${period === k ? "sr-btn-primary" : "sr-btn-ghost"}`}>{label}</button>
        ))}
      </div>
      {!d ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (
        <div className="sr-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.6fr 0.7fr 0.8fr 0.8fr 1fr 0.9fr", gap: 8, padding: "10px 14px", background: "var(--blue-light)", fontSize: 12, fontWeight: 700, color: "var(--navy)" }}>
            <span>#</span><span>Rep</span><span>Leads</span><span>Won</span><span>Converted</span><span>Pipeline</span><span>Avg 1st resp</span>
          </div>
          {(d.leaderboard || []).length === 0 ? <div className="sr-empty" style={{ padding: 28 }}>No ranked reps yet.</div> :
            d.leaderboard.map((r) => (
              <div key={r.owner_id} data-testid={`lb-row-${r.owner_id}`}
                style={{ display: "grid", gridTemplateColumns: "0.5fr 1.6fr 0.7fr 0.8fr 0.8fr 1fr 0.9fr", gap: 8, padding: "12px 14px", alignItems: "center", borderTop: "1px solid var(--border)" }}>
                <span style={{ fontWeight: 800, color: medal(r.rank) || "var(--muted)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  {medal(r.rank) ? <Medal size={14} style={{ color: medal(r.rank) }} /> : null}{r.rank}
                </span>
                <span style={{ fontWeight: 700, color: "var(--navy)" }}>{r.owner_name || "—"}</span>
                <span>{r.leads}</span>
                <span style={{ color: "#067647", fontWeight: 700 }}>{r.won}</span>
                <span>{r.converted} <span className="sr-muted" style={{ fontSize: 11 }}>({r.conversion_pct}%)</span></span>
                <span style={{ fontWeight: 700 }}>{money(r.pipeline_value)}</span>
                <span className="sr-muted" style={{ fontSize: 13 }}>{r.avg_first_response_hours != null ? `${r.avg_first_response_hours}h` : "—"}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ initial, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [category, setCategory] = useState(initial?.category || "");
  const [active, setActive] = useState(initial?.active !== false);
  const [win, setWin] = useState(initial?.send_window || { tz_offset_hours: -5, start_hour: 8, end_hour: 18, weekdays_only: true });
  const [steps, setSteps] = useState(initial?.steps?.length ? initial.steps : [{ channel: "email", day_offset: 0, subject: "", body: "" }]);
  const [busy, setBusy] = useState(false);
  const setStep = (i, k, v) => setSteps((s) => s.map((st, j) => j === i ? { ...st, [k]: v } : st));
  const addStep = () => setSteps((s) => [...s, { channel: "email", day_offset: (s[s.length - 1]?.day_offset || 0) + 2, subject: "", body: "" }]);
  const removeStep = (i) => setSteps((s) => s.filter((_, j) => j !== i));
  const save = async () => {
    if (!name.trim()) { toast.error("Name the template"); return; }
    setBusy(true);
    const body = { name, category: category.trim() || null, active, send_window: win, steps };
    try {
      if (initial?.id) await sr.nurtureTemplateUpdate(initial.id, body);
      else await sr.nurtureTemplateCreate(body);
      toast.success("Template saved"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save template"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sr-card" data-testid="template-editor" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <input className="sr-input" placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} data-testid="tmpl-name" style={{ flex: "1 1 220px" }} />
        <input className="sr-input" placeholder="Business category (blank = default for all)" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tmpl-category" style={{ flex: "1 1 220px" }} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="tmpl-active" /> Active
        </label>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12, alignItems: "center", fontSize: 13 }}>
        <span className="sr-muted">Send window:</span>
        <label>Start <input className="sr-input" type="number" min="0" max="23" value={win.start_hour} onChange={(e) => setWin({ ...win, start_hour: parseInt(e.target.value) || 0 })} data-testid="tmpl-win-start" style={{ width: 64, display: "inline-block" }} /></label>
        <label>End <input className="sr-input" type="number" min="1" max="24" value={win.end_hour} onChange={(e) => setWin({ ...win, end_hour: parseInt(e.target.value) || 24 })} data-testid="tmpl-win-end" style={{ width: 64, display: "inline-block" }} /></label>
        <label>UTC offset <input className="sr-input" type="number" value={win.tz_offset_hours} onChange={(e) => setWin({ ...win, tz_offset_hours: parseInt(e.target.value) || 0 })} data-testid="tmpl-win-tz" style={{ width: 64, display: "inline-block" }} /></label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="checkbox" checked={!!win.weekdays_only} onChange={(e) => setWin({ ...win, weekdays_only: e.target.checked })} data-testid="tmpl-win-weekdays" /> Weekdays only
        </label>
      </div>
      {steps.map((st, i) => (
        <div key={i} className="sr-card" style={{ background: "#F8FAFC", marginBottom: 8 }} data-testid={`tmpl-step-${i}`}>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <strong style={{ color: "var(--navy)" }}>Step {i + 1}</strong>
            <select className="sr-select" value={st.channel} onChange={(e) => setStep(i, "channel", e.target.value)} data-testid={`tmpl-step-channel-${i}`} style={{ maxWidth: 150 }}>
              <option value="email">Email</option>
              <option value="call_task">Call task</option>
            </select>
            <label style={{ fontSize: 13 }}>Day <input className="sr-input" type="number" min="0" value={st.day_offset} onChange={(e) => setStep(i, "day_offset", parseInt(e.target.value) || 0)} data-testid={`tmpl-step-day-${i}`} style={{ width: 70, display: "inline-block" }} /></label>
            {steps.length > 1 && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => removeStep(i)} data-testid={`tmpl-step-remove-${i}`} style={{ marginLeft: "auto" }}><Trash2 size={13} /></button>}
          </div>
          {st.channel === "email" && (
            <input className="sr-input" placeholder="Email subject" value={st.subject || ""} onChange={(e) => setStep(i, "subject", e.target.value)} data-testid={`tmpl-step-subject-${i}`} style={{ marginBottom: 6 }} />
          )}
          <textarea className="sr-input" placeholder={st.channel === "email" ? "Email body" : "Call task note for the rep"} value={st.body || ""} onChange={(e) => setStep(i, "body", e.target.value)} data-testid={`tmpl-step-body-${i}`} rows={3} />
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={addStep} data-testid="tmpl-add-step"><Plus size={13} /> Add step</button>
        <button className="sr-btn sr-btn-primary sr-btn-sm" disabled={busy} onClick={save} data-testid="tmpl-save" style={{ marginLeft: "auto" }}>{busy ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />} Save template</button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onCancel} data-testid="tmpl-cancel">Cancel</button>
      </div>
    </div>
  );
}

function NurtureTemplates() {
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(null); // template obj or {} for new
  const load = () => sr.nurtureTemplates().then(setD).catch(() => setD({ templates: [] }));
  useEffect(() => { load(); }, []);
  const del = async (id) => { try { await sr.nurtureTemplateDelete(id); toast.success("Template deleted"); load(); } catch { toast.error("Couldn't delete"); } };
  return (
    <div data-testid="nurture-templates">
      <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
        <p className="sr-muted" style={{ margin: 0, fontSize: 13, flex: 1 }}>Customize the nurture emails, timing and send window. A template with a matching business category is used first; a blank-category template is the default for everyone.</p>
        {!editing && <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setEditing({})} data-testid="tmpl-new"><Plus size={13} /> New template</button>}
      </div>
      {editing && <TemplateEditor initial={editing.id ? editing : null} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      {!d ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (d.templates || []).length === 0 && !editing ? (
        <div className="sr-empty" style={{ padding: 24 }}>No custom templates yet — leads use the built-in 3-touch default. Add one to tailor by category.</div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {(d.templates || []).map((t) => (
            <div key={t.id} className="sr-card" data-testid={`tmpl-row-${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <strong style={{ color: "var(--navy)" }}>{t.name}</strong>
              <span className="sr-badge sr-badge-gray">{t.category || "Default (all)"}</span>
              <span className="sr-badge" style={{ background: t.active ? "#F1FBF4" : "#F3F4F6", color: t.active ? "#067647" : "#6B7280", border: "1px solid " + (t.active ? "#B6E3C6" : "#E5E7EB") }}>{t.active ? "Active" : "Inactive"}</span>
              <span className="sr-muted" style={{ fontSize: 13 }}>{(t.steps || []).length} steps</span>
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setEditing(t)} data-testid={`tmpl-edit-${t.id}`}>Edit</button>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => del(t.id)} data-testid={`tmpl-delete-${t.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Sales() {
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const view = tab === "pipeline" ? "all" : tab;

  const load = useCallback(() => {
    if (tab === "reports" || tab === "dashboard" || tab === "followups" || tab === "leaderboard" || tab === "nurture") { setData({ leads: [] }); return; }
    sr.salesLeads({ view, q: q || undefined }).then(setData).catch(() => setData({ leads: [], counts: {}, role: "rep" }));
  }, [tab, view, q]);
  useEffect(() => { load(); }, [load]);

  const role = data?.role || _derivedRole();
  const tabs = [["dashboard", "Dashboard"], ["mine", "My Leads"], ["available", "Available"], ...(role !== "rep" ? [["all", "All Leads"]] : []), ["pipeline", "Pipeline"], ["followups", "Follow-ups"], ...(role !== "rep" ? [["leaderboard", "Leaderboard"], ["nurture", "Nurture"]] : []), ["reports", "Reports"]];

  return (
    <div>
      <Head title="Sales" sub="Every inquiry, demo and quote — routed to a salesperson, tracked through the pipeline, converted to a customer." />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sales-tab-${k}`}
            className={`sr-btn sr-btn-sm ${tab === k ? "sr-btn-primary" : "sr-btn-ghost"}`}>{label}</button>
        ))}
      </div>

      {tab === "dashboard" ? <Dashboard /> : tab === "reports" ? <Reports /> : tab === "leaderboard" ? <Leaderboard /> : tab === "nurture" ? <NurtureTemplates /> : tab === "followups" ? <FollowUps onOpen={setOpen} /> : !data ? <div className="sr-empty"><Loader2 className="sr-spin" /></div> : (
        <>
          {tab !== "pipeline" && (
            <div style={{ position: "relative", maxWidth: 320, marginBottom: 12 }}>
              <Search size={15} style={{ position: "absolute", left: 10, top: 11, color: "var(--muted)" }} />
              <input className="sr-input" style={{ paddingLeft: 32 }} placeholder="Search name, business, email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sales-search" />
            </div>
          )}

          {tab === "pipeline" ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6,minmax(150px,1fr))", gap: 10, overflowX: "auto" }} data-testid="sales-pipeline">
              {STAGES.map((s) => {
                const items = (data.leads || []).filter((l) => l.stage === s);
                return (
                  <div key={s} style={{ background: "#F8FAFC", borderRadius: 10, padding: 8, minHeight: 120 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><StageBadge stage={s} /><strong className="sr-muted">{items.length}</strong></div>
                    {items.map((l) => (
                      <button key={l.id} onClick={() => setOpen(l.id)} data-testid={`pipe-card-${l.id}`}
                        style={{ display: "block", width: "100%", textAlign: "left", background: "#fff", border: "1px solid var(--border)", borderRadius: 8, padding: 8, marginBottom: 8, cursor: "pointer" }}>
                        <strong style={{ fontSize: 13, color: "var(--navy)" }}>{(l.contact || {}).name || (l.business || {}).name || "Lead"}</strong>
                        <span className="sr-muted" style={{ display: "block", fontSize: 11 }}>{l.owner_name || "Unassigned"}{l.expected_value ? ` · ${money(l.expected_value)}` : ""}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="sr-card" style={{ padding: 0, overflow: "hidden" }} data-testid="sales-leads-table">
              <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1.3fr 0.8fr 1fr 0.9fr", gap: 8, padding: "10px 14px", background: "var(--blue-light)", fontSize: 12, fontWeight: 700, color: "var(--navy)" }}>
                <span>Lead</span><span>Source</span><span>Stage</span><span>Owner</span><span>Value</span>
              </div>
              {(data.leads || []).length === 0 ? <div className="sr-empty" style={{ padding: 28 }}>No leads here yet.</div> :
                data.leads.map((l) => <LeadRow key={l.id} lead={l} onOpen={(x) => setOpen(x.id)} />)}
            </div>
          )}
        </>
      )}

      {open && <LeadDetail leadId={open} role={role} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
